import React from "react";
import SiteShell from "@/components/SiteShell";

/** API reference (www.frazzlecrm.com/docs). Pro plan feature. */

function H({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-10 border-b border-slate-100 pb-1 text-[20px] font-bold text-slate-900">
      {children}
    </h2>
  );
}

function EP({
  method,
  path,
  children,
}: {
  method: string;
  path: string;
  children?: React.ReactNode;
}) {
  const color =
    method === "GET"
      ? "bg-emerald-100 text-emerald-700"
      : method === "POST"
        ? "bg-indigo-100 text-indigo-700"
        : method === "PUT"
          ? "bg-amber-100 text-amber-700"
          : "bg-red-100 text-red-600";
  return (
    <div className="mt-3 rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2">
        <span
          className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ${color}`}
        >
          {method}
        </span>
        <code className="font-mono text-[13px] text-slate-700">{path}</code>
      </div>
      {children && (
        <div className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
          {children}
        </div>
      )}
    </div>
  );
}

function Code({ children }: { children: string }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 font-mono text-[12px] leading-relaxed text-slate-100">
      {children}
    </pre>
  );
}

export default function ApiDocs() {
  return (
    <SiteShell title="API Reference — FrazzleCRM">
      <section className="mx-auto max-w-3xl px-6 pb-24 pt-16">
        <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900">
          API Reference
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
          Programmatic access to your FrazzleCRM workspace. The public API and
          MCP are available on the <b>Pro plan and up</b>. Base URL:{" "}
          <code className="rounded bg-slate-100 px-1 font-mono text-[13px]">
            https://app.frazzlecrm.com
          </code>
        </p>

        <H>Authentication</H>
        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
          Create an API key in <b>Settings → API keys</b> (keys look like{" "}
          <code className="font-mono text-[12.5px]">frz_…</code> and are shown
          once). Send it on every request:
        </p>
        <Code>{`curl https://app.frazzlecrm.com/api/v1/contacts \\
  -H "Authorization: Bearer frz_your_key"
# or: -H "x-api-key: frz_your_key"`}</Code>

        <H>Contacts</H>
        <EP method="GET" path="/api/v1/contacts">
          List contacts. Query: <code>?limit=</code>, <code>?search=</code>.
        </EP>
        <EP method="POST" path="/api/v1/contacts">
          Create/upsert a contact (deduped by your company&apos;s policy —
          email, phone, or email-then-phone).
          <Code>{`{ "firstName": "Ada", "email": "ada@example.com",
  "phone": "+15551234567", "tags": ["vip"],
  "fields": { "plan": "pro" } }`}</Code>
        </EP>
        <EP method="GET" path="/api/v1/contacts/:id">
          Fetch one contact (tags, custom fields, DND state).
        </EP>
        <EP method="PUT" path="/api/v1/contacts/:id">
          Update fields/tags. <b>DELETE</b> removes the contact.
        </EP>

        <H>Automations</H>
        <EP method="GET" path="/api/v1/automations">
          List automations with status and triggers.
        </EP>
        <EP method="POST" path="/api/v1/automations/:id/trigger">
          Enroll a contact into an automation.
          <Code>{`{ "email": "ada@example.com" }   // or { "contactId": "..." }`}</Code>
        </EP>

        <H>Appointments</H>
        <EP method="POST" path="/api/v1/appointments">
          Push a booking from an external scheduler. Fires
          appointment-booked triggers; flexible date parsing (ISO, epoch, US
          formats — zone-less times use your company timezone).
          <Code>{`{ "email": "ada@example.com", "title": "Demo call",
  "startsAt": "2026-08-01T15:00:00Z", "status": "booked" }`}</Code>
        </EP>

        <H>Relationships</H>
        <EP method="GET" path="/api/v1/relationship-types">
          Your relationship vocabulary (Parent/Child, custom…).
        </EP>
        <EP method="POST" path="/api/v1/relationships">
          Link two contacts. <b>DELETE /api/v1/relationships/:id</b> unlinks.
        </EP>

        <H>Inbound webhooks (no API key needed)</H>
        <EP method="POST" path="/api/wh/:webhookId">
          The URL minted by an Inbound Webhook node. POST any JSON: the
          contact is upserted from <code>email</code>/<code>phone</code>/
          <code>name</code>/<code>tags</code>/<code>fields</code>, the FULL
          payload becomes <code>{"{{webhook.*}}"}</code> merge fields, an
          embedded appointment is ingested, and the wired automation enrolls
          the contact when live. Drafts capture test samples.
        </EP>

        <H>Trigger links</H>
        <EP method="GET" path="/api/l/:linkId?email=…">
          A tracked redirect: records who clicked, fires any
          &ldquo;Trigger link is clicked&rdquo; automations watching the
          link, then 302s to the destination (utm_* params pass through).
        </EP>

        <H>MCP (AI access)</H>
        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">
          Connect Claude or any MCP-capable client to{" "}
          <code className="font-mono text-[12.5px]">
            POST https://app.frazzlecrm.com/api/mcp
          </code>{" "}
          with your API key as a Bearer token. Your AI can read the funnel
          map, audit automations, and create/update flows (drafts by
          default). Call <code className="font-mono text-[12.5px]">get_schema_reference</code>{" "}
          first — it documents every trigger, step and rule shape.
        </p>

        <p className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-4 text-[13px] leading-relaxed text-slate-500">
          Questions or missing endpoints? Email{" "}
          <a href="mailto:support@frazzlecrm.com" className="text-indigo-600 hover:underline">
            support@frazzlecrm.com
          </a>
          . Rate limits are generous; be sensible.
        </p>
      </section>
    </SiteShell>
  );
}
